import { Card, Grid, MultiSelect, Stack, Switch, Text } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { AVAILABLE_TRACKING_EVENTS } from '../../constants/Facebook';
import { CONVERSION_EVENTS } from '../../constants/conversionEvents';
import standardEventTypes from '../../constants/eventTypes';
import { conversionTypesOf } from '../../helpers/customEventConversions';
import useFetch from '../../helpers/useFetch';
import FacebookPixelStatus from './FacebookPixelStatus';

const FacebookTrackingSettings = ({ facebookAccount, handleChange }) => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const domain = useSelector((state) => state.domain.domain);
  const [customEventOptions, setCustomEventOptions] = useState([]);

  useEffect(() => {
    const standardIds = new Set(standardEventTypes.map((e) => e.value));

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/eventActions`)
      .then(({ eventActions }) => {
        setCustomEventOptions(
          (eventActions || [])
            .filter((action) => !standardIds.has(action) && !action.startsWith('Catalog trigger - '))
            .sort()
            .map((action) => `custom/${action}`)
        );
      })
      .catch(() => setCustomEventOptions([]));
  }, [fetch, accountId, domainId]);

  const backendTracking = facebookAccount?.backendTracking ?? false;
  const enabledTrackingEvents = facebookAccount?.enabledTrackingEvents || [];

  // Ad-conversion event names (Meta forwarding targets). Analytics-only names
  // (Purchase / AddToCart / CartCreate) are NOT in this set — they're Releva
  // classifications, forwarded array-side by the backend, not raw-Meta events.
  const conversionValues = useMemo(() => new Set(CONVERSION_EVENTS.map((e) => e.value)), []);

  // The domain-level mapping (Settings -> Analytics) is keyed by the client's
  // bare action name (no `custom/` prefix). Only entries mapped to a real
  // ad-conversion type count as "mapped" for the raw Meta pickers — a
  // half-configured row (no event yet) or an analytics-only classification
  // (e.g. Engagement with a fixed value, which writes an analytics event name)
  // must still appear in the pickers below, so attaching a value never silently
  // removes a custom event from Facebook.
  const conversions = domain?.customEventConversions;
  const mappedActions = useMemo(
    () =>
      new Set(
        Object.entries(conversions || {})
          .filter(([, cfg]) => conversionTypesOf(cfg).some((type) => conversionValues.has(type)))
          .map(([action]) => action)
      ),
    [conversions, conversionValues]
  );

  // If a custom event was selected raw (`custom/foo`) before `foo` was mapped in
  // Settings -> Analytics, drop the stale raw selection so it isn't sent both
  // raw and as its mapped conversion (which double-counts in Meta).
  useEffect(() => {
    const pruned = enabledTrackingEvents.filter((value) => !mappedActions.has(value.replace(/^custom\//, '')));
    if (pruned.length !== enabledTrackingEvents.length) {
      handleChange({ target: { name: 'enabledTrackingEvents', value: pruned } });
    }
  }, [enabledTrackingEvents, mappedActions, handleChange]);

  // Exclude already-mapped actions from the raw Tracked events so the same custom
  // event can't be sent both raw and as a mapped conversion (which double-counts).
  const trackableCustomEvents = customEventOptions.filter((o) => !mappedActions.has(o.replace(/^custom\//, '')));

  // Ad-conversion events mapped for this domain (plus stale enabled ones, kept
  // deselectable), minus the names already offered as standard events —
  // Mantine rejects duplicate values across groups. Only ad-conversion types
  // belong here: Purchase/AddToCart/CartCreate map entries are Releva-analytics
  // classifications with no Meta forwarding, so they're filtered out.
  const standardTrackingEvents = new Set(AVAILABLE_TRACKING_EVENTS);
  const mappedConversionEvents = [
    ...new Set([
      ...Object.values(conversions || {})
        .flatMap(conversionTypesOf)
        .filter((type) => conversionValues.has(type)),
      ...enabledTrackingEvents.filter((value) => conversionValues.has(value)),
    ]),
  ].filter((value) => !standardTrackingEvents.has(value));

  const trackedEventOptions = [
    { group: 'Meta standard events', items: AVAILABLE_TRACKING_EVENTS },
    ...(mappedConversionEvents.length ? [{ group: 'Mapped conversions', items: mappedConversionEvents }] : []),
    ...(trackableCustomEvents.length ? [{ group: 'Custom events', items: trackableCustomEvents }] : []),
  ];

  return (
    <Card withBorder>
      <Card.Section inheritPadding py="sm">
        <Stack gap="md">
          <Grid>
            <Grid.Col span={4}>
              <Text fw={500}>Server-side tracking (CAPI)</Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <Switch
                checked={backendTracking}
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: 'backendTracking',
                      value: e.currentTarget.checked,
                    },
                  })
                }
              />
              <Text size="sm" c="dimmed" mt="xs">
                Send the tracked events to Meta from Releva&apos;s servers (Conversions API).
              </Text>
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={4}>
              <Text fw={500}>Tracked events</Text>
            </Grid.Col>
            <Grid.Col span={8}>
              <MultiSelect
                data={trackedEventOptions}
                value={enabledTrackingEvents}
                onChange={(value) => handleChange({ target: { name: 'enabledTrackingEvents', value } })}
                placeholder="Pick Meta events or your custom events..."
                aria-label="Tracked events"
                disabled={!backendTracking}
                searchable
                clearable
              />
              <Text size="sm" c="dimmed" mt="xs">
                Only selected events are sent to Meta — leave out anything the site already sends itself. Mapped
                conversions come from Settings → Analytics → Custom conversions; a raw custom event is sent as its bare
                name, which Meta can&apos;t optimize for.
              </Text>
            </Grid.Col>
          </Grid>

          <FacebookPixelStatus
            pixelId={facebookAccount?.pixelId}
            enabledTrackingEvents={facebookAccount?.enabledTrackingEvents || []}
          />
        </Stack>
      </Card.Section>
    </Card>
  );
};

export default FacebookTrackingSettings;
